import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import VoteButtons from "components/card/VoteButtons";
import usePredictionVotes from "hooks/predictions/usePredictionVotes";
import useAuth from "hooks/auth/useAuth";
import PointsDisplay from "components/card/PointsDisplay";
import EndDate from "components/date/EndDate";
import { calculatePoints } from "libs/utils";
import useLikes from "hooks/predictions/useLikes";
import CommentSection from "components/social/CommentSection";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import useDarkMode from "hooks/useDarkMode";
import { collection, query, onSnapshot, getDocs } from "firebase/firestore";
import { db } from "config/firebase";
import Dialog from "@mui/material/Dialog";
import { useMediaQuery, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const PredictionCard = ({ prediction }) => {
  const router = useRouter();
  const { handleVote } = usePredictionVotes();
  const { currentUser } = useAuth();
  const [currentCard, setCurrentCard] = useState(prediction);
  const { agreeCount, disagreeCount } = currentCard.votes
    ? calculatePoints(currentCard.votes)
    : 0;

  useEffect(() => {
    setCurrentCard(prediction);
  }, [prediction]);

  const { likeCount, toggleLike, checkIfLiked } = useLikes(
    `predictions/${prediction.id}`,
  );
  const isLiked = checkIfLiked(currentUser?.uid);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const { theme: appTheme } = useDarkMode();
  const isLight = appTheme === "light";

  useEffect(() => {
    if (!prediction.id) return;
    const commentsRef = collection(
      db,
      "predictions",
      prediction.id,
      "comments",
    );
    // Use a one-time fetch instead of onSnapshot for the card view
    getDocs(commentsRef).then((snapshot) => {
      setCommentCount(snapshot.size);
    });
  }, [prediction.id]);

  const givenTime = new Date(currentCard?.endOfVoting);
  const now = new Date();
  const isPast = givenTime < now;
  //style={{ backgroundImage: `url(${section.imageUrl})` }}
  return (
    <div
      style={{ fontFamily: "Noto Sans Armenian" }}
      className={`relative flex flex-col justify-between gap-3 sm:gap-4 p-4 sm:p-5 md:p-6 rounded-2xl sm:rounded-[28px] bg-white dark:bg-[#0c0a1f] shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-slate-100 dark:border-white/10 hover:shadow-lg transition-all duration-300 w-full h-full ${
        currentCard.type === "finished"
          ? currentCard.result === true
            ? "border-emerald-200 dark:border-emerald-800/50"
            : "border-red-200 dark:border-red-800/50"
          : ""
      }`}
    >
      <div
        onClick={() => router.push(`/predictions/${currentCard.id}`)}
        className="flex flex-row gap-3 sm:gap-4 md:gap-6 justify-start cursor-pointer w-full min-w-0"
      >
        <div className="flex flex-col gap-1.5 sm:gap-2 shrink-0 w-[80px] sm:w-[100px] md:w-[140px]">
          <div className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] md:w-[140px] md:h-[140px] relative overflow-hidden rounded-xl sm:rounded-[20px]">
            <Image
              width={140}
              height={140}
              src={
                currentCard.image ||
                currentCard.predictor?.image ||
                "/default_video.webp"
              }
              alt="person"
              className="rounded-xl sm:rounded-[20px] object-cover w-full h-full"
            />
          </div>

          {currentCard.predictor && (
            <div className="flex flex-col items-start gap-0.5 mt-0.5 sm:mt-1 min-w-0">
              <span
                title={`${currentCard.predictor?.firstName} ${currentCard.predictor?.lastName}`}
                className="text-slate-900 dark:text-slate-200 text-[11px] sm:text-xs md:text-sm font-bold truncate w-full"
              >
                {currentCard.predictor?.firstName} {currentCard.predictor?.lastName}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5 sm:gap-2 flex-1 w-full min-w-0">
          <div className="flex justify-between items-start w-full gap-2">
            <h2 className="text-slate-900 dark:text-white text-sm sm:text-base md:text-lg font-bold tracking-tight line-clamp-2 leading-snug break-words">
              {prediction?._highlightResult?.title?.value ? (
                <span
                  className="highlighted"
                  dangerouslySetInnerHTML={{
                    __html: prediction?._highlightResult?.title?.value || "",
                  }}
                />
              ) : (
                currentCard.title
              )}
            </h2>

            {currentCard.type === "finished" ? (
              currentCard.result === true ? (
                <span className="text-[9px] sm:text-[10px] uppercase text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                  Ճիշտ է
                </span>
              ) : (
                <span className="text-[9px] sm:text-[10px] uppercase text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/40 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                  Սխալ է
                </span>
              )
            ) : (
              isPast && (
                <span className="text-[9px] sm:text-[10px] uppercase text-red-500 font-bold bg-red-50 dark:bg-red-950/40 dark:text-red-400 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                  Ավարտված
                </span>
              )
            )}
          </div>

          <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm line-clamp-2 sm:line-clamp-3 leading-relaxed font-normal mt-0.5 sm:mt-1 break-words">
            {currentCard.description}
          </p>
        </div>
      </div>

      {/* Card Footer Row matching screenshot */}
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-100 dark:border-white/5">
        <EndDate deadline={currentCard?.deadline || currentCard?.endOfVoting} />

        <VoteButtons
          isDisabled={isPast || currentCard.type === "finished"}
          currentUser={currentUser}
          state={currentCard.votes?.[currentUser?.uid] || ""}
          onChange={(state) => {
            handleVote(currentCard.id, currentUser?.uid, state);
            setCurrentCard((prev) => ({
              ...prev,
              votes: {
                ...prev.votes,
                [currentUser?.uid]: state,
              },
            }));
          }}
        />
      </div>

      <div className="flex flex-row items-center gap-4 sm:gap-6 mt-2 sm:mt-4 pt-2 sm:pt-4 border-t border-cyan-500/10 px-1 sm:px-2">
        <button
          onClick={() => toggleLike(currentUser?.uid)}
          className={`flex items-center gap-2 text-sm font-bold transition-all duration-300 ${isLiked ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(102,133,255,0.5)] scale-110" : "text-slate-400 hover:text-cyan-400"}`}
        >
          {isLiked ? (
            <ThumbUpIcon sx={{ fontSize: 20 }} />
          ) : (
            <ThumbUpOutlinedIcon sx={{ fontSize: 20 }} />
          )}
          <span>{likeCount || 0}</span>
        </button>

        <button
          onClick={() => setShowComments(!showComments)}
          className={`flex items-center gap-2 text-sm font-bold transition-all duration-300 ${showComments ? "text-cyan-400" : "text-slate-400 hover:text-cyan-400"}`}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
          <span>{commentCount || 0}</span>
        </button>
      </div>

      <Dialog
        open={showComments}
        onClose={() => setShowComments(false)}
        fullScreen={useMediaQuery(useTheme().breakpoints.down("sm"))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: isLight ? "rgba(255, 255, 255, 0.95)" : "#030014",
            backgroundImage: isLight
              ? "none"
              : "linear-gradient(to bottom, rgba(102, 133, 255, 0.05), transparent)",
            border: isLight
              ? "1px solid rgba(0, 0, 0, 0.1)"
              : "1px solid rgba(102, 133, 255, 0.2)",
            borderRadius: useMediaQuery(useTheme().breakpoints.down("sm"))
              ? 0
              : 24,
            boxShadow: "0 0 40px rgba(0,0,0,0.2)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <div className="flex justify-between items-center p-4 border-b border-cyan-500/10 bg-[#030014] light:bg-white light:border-slate-200">
          <h3 className="text-cyan-50 light:text-slate-900 font-bold tracking-wider">
            Մեկնաբանություններ ({commentCount})
          </h3>
          <IconButton
            onClick={() => setShowComments(false)}
            sx={{
              color: isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.7)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="overflow-y-auto bg-transparent">
          <CommentSection
            docPath={`predictions/${prediction.id}`}
            isInModal={true}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default PredictionCard;
